import React, { useEffect } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import {
  Avatar,
  Button,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import pinnedClients from "@/components/Sidebar/ClickComponents/Client/store/PinnedClients";
import { graphql } from "@/gql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODateTime } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import RemarkForm, { RemarkValues } from "./RemarkForm";
import TimelineEntry from "./TimelineEntry";
import TrackingStatusSelect from "./TrackingStatusSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
}

const clientTimelineQuery = graphql(`
  query ClientTimeline($id: ID!) {
    client(id: $id) {
      id
      email
      profile {
        id
        fullName
        mobileNumber
        avatarUrl
      }
      trackingStatus
      events(order: DESC) {
        nodes {
          ...TimelineEntry
        }
      }
    }
  }
`);

const createEventRemarkMutation = graphql(`
  mutation CreateEventRemark($input: CreateEventRemarkInput!, $clientId: ID!) {
    createEventRemark(input: $input) {
      event {
        ...TimelineEntry
      }
      query {
        client(id: $clientId) {
          id
          lastEvent {
            ...TimelineEntry
          }
          events(order: DESC) {
            nodes {
              ...TimelineEntry
            }
          }
        }
      }
    }
  }
`);

const useStyles = makeStyles()({
  root: {
    width: 472,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    overflowY: "hidden",
  },
  closeButton: {
    marginLeft: "auto",
  },
  profile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  noPadding: {
    padding: 0,
  },
  noShrink: {
    flexShrink: 0,
  },
  status: {
    marginTop: 24,
  },
  statusLabel: {
    marginBottom: 12,
  },
  timelineLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
  },
  timelineContainer: {
    border: "1px solid #DEE6F0",
    marginTop: 12,
    overflowY: "auto",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 100,
  },
  timeline: {
    padding: "0px 20px",
    m: 0,
    [`& .${timelineItemClasses.root}:before`]: {
      flex: 0,
      padding: 0,
    },
  },
  timelineDot: {
    marginBottom: 0,
  },
  timelineContent: {
    paddingRight: 0,
  },
});

const TimelineDrawer: React.FC<Props> = ({ open, onClose, clientId }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const newRemarkForm = useDialogState();

  const [fetch, { loading, data }] = useLazyQuery(clientTimelineQuery, {
    variables: { id: clientId },
  });
  useEffect(() => {
    if (open) fetch();
  }, [open]);

  const [createEventRemark] = useMutation(createEventRemarkMutation);
  const createRemark = async (values: RemarkValues) => {
    try {
      await createEventRemark({
        variables: {
          input: {
            comment: values.comment,
            createdAt: formatISODateTime(values.createdAt),
            clientId,
          },
          clientId,
        },
      });
      newRemarkForm.hide();
      snackbar.open("Remark successfully added.");
    } catch (e) {
      snackbar.open(displayError(e, "Unable to save remark."));
    }
  };

  return (
    <Drawer open={open} onClose={onClose} anchor="right">
      <div className={classes.root}>
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <div className={classes.profile}>
          <ListItem className={classes.noPadding}>
            <ListItemAvatar>
              <Avatar src={data?.client?.profile?.avatarUrl ?? undefined} />
            </ListItemAvatar>
            <ListItemText
              primary={data?.client?.profile?.fullName}
              secondary={
                <>
                  <div>{data?.client?.email}</div>
                  <div>{data?.client?.profile?.mobileNumber}</div>
                </>
              }
            />
          </ListItem>
          <Fab
            className={classes.noShrink}
            color={pinnedClients.has(clientId) ? "primary" : "default"}
            size="small"
            onClick={() => pinnedClients.toggle(clientId)}
          >
            <PushPinOutlinedIcon fontSize="small" />
          </Fab>
        </div>
        <div className={classes.status}>
          <Typography className={classes.statusLabel}>Status</Typography>
          <TrackingStatusSelect
            clientId={clientId}
            value={data?.client?.trackingStatus}
          />
        </div>
        <div className={classes.timelineLabel}>
          <Typography>Timeline</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={newRemarkForm.show}
          >
            Add Remarks
          </Button>
        </div>
        <div className={classes.timelineContainer}>
          {loading && !data ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : (
            <Timeline className={classes.timeline}>
              {newRemarkForm.shown && (
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot className={classes.timelineDot} />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent className={classes.timelineContent}>
                    <RemarkForm
                      onCancel={newRemarkForm.hide}
                      onSubmit={createRemark}
                    />
                  </TimelineContent>
                </TimelineItem>
              )}
              {data?.client?.events.nodes.map((event, i) => (
                <TimelineEntry
                  key={event.id}
                  eventId={event.id}
                  clientId={clientId}
                  isFirst={!newRemarkForm.shown && i === 0}
                  isLast={i + 1 === data.client?.events.nodes.length}
                />
              ))}
            </Timeline>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default TimelineDrawer;
